import React, { useState, useEffect, memo, useCallback, useContext, useRef, useMemo } from "react";
import Drawer from "@material-ui/core/Drawer";

import { callApi, convertDateTime, blockEmoji } from "bodycodi/cmap/app/js/common";
import { DispatchContext, StateContext } from "bodycodi/cmap/app/components/common/context";
import PickerHeader from "bodycodi/cmap/app/components/common/pickerHeader";
import TimePicker, { getPickerTime } from "bodycodi/cmap/app/components/cmap2040/timePicker";
import icon_alert_triangle from "bodycodi/cmap/app/svg/icon_alert_triangle.svg";
import { useSelector } from "react-redux";
import { RootState } from "bodycodi/cmap/app/redux/store";
import SelectTimePop from "bodycodi/cmap/app/components/cmap2040/SelectTimePop";

type ChooseTimePickerProp = {
  scheduleType: string;
  setEndTime: any;
  setStartTime: any;
  setScheduleTitle: any;
  setCsDecs: any;
  step: number;
  chooseDate: any;
  startTime: any;
  endTime: any;
  scheduleTitle: string;
  csDecs: string;
  children: any;
  isAvailableTime: boolean;
  setIsAvailableTime: any;
  seqMember: number;
};

const ChooseTimePicker = ({
  seqMember,
  setStartTime,
  setEndTime,
  setScheduleTitle,
  setCsDecs,
  scheduleType,
  step,
  chooseDate,
  startTime,
  endTime,
  scheduleTitle,
  csDecs,
  isAvailableTime,
  setIsAvailableTime,
  children,
}: ChooseTimePickerProp) => {
  const { seqPartner, seqPartnerBranch, seqPartnerCoach } = useSelector(
    (state: RootState) => state.partner
  );
  const { dispatch } = useContext(DispatchContext);
  const { state } = useContext(StateContext);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"시작" | "종료">("시작");
  const target = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { startHours, startMinutes, endHours, endMinutes } = getPickerTime();

  useEffect(() => {
    if (
      (step === 3 && scheduleType === "ET") ||
      (step === 4 && scheduleType === "OT") ||
      (step === 4 && scheduleType === "CS")
    ) {
      setStartTime({
        hour: startHours,
        min: startMinutes,
      });
      setEndTime({
        hour: endHours,
        min: endMinutes,
      });
    }
  }, [step]);

  useEffect(() => {
    const fetchCheckAvailableTime = async (startTime: any, endTime: any) => {
      if (
        seqPartnerCoach === 0 ||
        typeof startTime.hour !== "number" ||
        typeof endTime.hour !== "number"
      )
        return;
      if (startTime.hour * 60 + startTime.min === endTime.hour * 60 + endTime.min) return;
      const statDateTime = convertDateTime(chooseDate, startTime);
      const endDateTime = convertDateTime(chooseDate, endTime);
      callApi(
        "/schedule/reservation/check-available-time",
        {
          appointmentClCd: scheduleType,
          seqPartner,
          seqPartnerBranch,
          seqPartnerCoach,
          seqMember: seqMember,
          startDate: statDateTime,
          endDate: endDateTime,
          startDt: statDateTime.split(" ")[0],
          endDt: endDateTime.split(" ")[0],
          startTime: `${startTime.hour?.toString().padStart(2, "0")}:${startTime.min
            ?.toString()
            .padStart(2, "0")}`,
          endTime: `${endTime.hour?.toString().padStart(2, "0")}:${endTime.min
            ?.toString()
            .padStart(2, "0")}`,
          scheduleName: "",
          memo: "",
        },
        "POST"
      ).then((res) => {
        if (res.MsgVO) {
          if (res.MsgVO.msgCd === "CMAP2040-00") {
            setIsAvailableTime(true);
          } else if (res.MsgVO.msgCd === "CMAP2040-18") {
            setIsAvailableTime(false);
          }
        }
      });
    };
    const startTimelMinTotal = startTime.hour * 60 + startTime.min;
    const endTimeMinTotal = endTime.hour * 60 + endTime.min;
    if (startTimelMinTotal >= endTimeMinTotal) {
      if (startTimelMinTotal + 60 >= 1440) {
        setEndTime({
          hour: 23,
          min: 55,
        });
        fetchCheckAvailableTime(startTime, {
          hour: 23,
          min: 55,
        });
        return;
      }
      setEndTime({
        hour: Math.floor((startTimelMinTotal + 60) / 60),
        min: (startTimelMinTotal + 60) % 60,
      });
      fetchCheckAvailableTime(startTime, {
        hour: Math.floor((startTimelMinTotal + 60) / 60),
        min: (startTimelMinTotal + 60) % 60,
      });
      dispatch({
        type: "UPDATE",
        data: { toast: true, toastText: "<span>유효한 시간으로 변경됩니다.</span>" },
      });
      return;
    }

    fetchCheckAvailableTime(startTime, endTime);
  }, [startTime, endTime]);

  useEffect(() => {
    if (scheduleType === "APPOINTMENT") {
      setTitle("개인레슨 스케줄 예약");
    } else if (scheduleType === "OT") {
      setTitle("OT 스케줄 예약");
    } else if (scheduleType === "CS") {
      setTitle("상담 스케줄 예약");
    } else {
      setTitle("기타 스케줄 추가");
    }
  }, [scheduleType]);

  const handleClose = useCallback(() => {
    dispatch({ type: "CLOSE_ALL_POP" });
  }, []);

  const handleChangeTime = (typeName: "시작" | "종료") => {
    setType(typeName);
    dispatch({ type: "POP_UP_OPEN", id: "NOTIFICATION_POP", open: { selectTimePop: true } });
  };

  const handleChangeTextTitle = (e: any) => {
    if (textAreaRef.current) {
      textAreaRef.current.value = blockEmoji(textAreaRef.current.value);
      if (textAreaRef.current.value.length > 40) {
        textAreaRef.current.value = textAreaRef.current.value.substring(0, 40);
        return;
      }
      if (textAreaRef.current.scrollHeight > 80) return;
      textAreaRef.current.style.height = "5px";
      textAreaRef.current.style.height = textAreaRef.current?.scrollHeight + "px";
      setScheduleTitle(textAreaRef.current.value);
    }
  };

  const handleChangeTextMemo = (e: any) => {
    const updatedValue = blockEmoji(e.currentTarget.value);
    setCsDecs(updatedValue);
  };

  const handleFocus = () => {
    dispatch({
      type: "UPDATE",
      data: { isShowBottom: false },
    });
  };
  const handleBlur = () => {
    dispatch({
      type: "UPDATE",
      data: { isShowBottom: true },
    });
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={state.chooseTimePickerPop}
        onClose={handleClose}
        className="picker-pop max"
      >
        <PickerHeader title={title} onClose={handleClose} />
        <div className="picker-body choose-time-pop add-schedule-pop-wrap" ref={target}>
          <div className="add-schedule-pop padding">
            <div className="title">시간 선택</div>
            <TimePicker
              typeName="시작"
              chooseDate={chooseDate}
              currentHours={`${startTime.hour}`.padStart(2, "0")}
              currentMinutes={`${startTime.min}`.padStart(2, "0")}
              handleChangeTime={handleChangeTime}
            />
            <TimePicker
              typeName="종료"
              chooseDate={chooseDate}
              currentHours={`${endTime.hour}`.padStart(2, "0")}
              currentMinutes={`${endTime.min}`.padStart(2, "0")}
              handleChangeTime={handleChangeTime}
            />
            {!isAvailableTime && (
              <div className="holiday">
                <img src={icon_alert_triangle} />
                <span>이미 예약된 스케줄이 있어요!</span>
              </div>
            )}

            <div className="title padding">스케줄 제목</div>
            <textarea
              className="time-picker-title"
              placeholder="내 스케줄 리스트에 보여져요!"
              onChange={handleChangeTextTitle}
              onFocus={handleFocus}
              onBlur={handleBlur}
              ref={textAreaRef}
              maxLength={40}
            ></textarea>
            <div className="textarea-length">{scheduleTitle.length}/40</div>
            <div className="title padding">상세 메모</div>
            <textarea
              className="time-picker-textarea"
              placeholder="상세 내용을 입력해주세요.(선택)"
              onChange={handleChangeTextMemo}
              onFocus={handleFocus}
              onBlur={handleBlur}
              maxLength={1000}
              value={csDecs}
            />
            <div className="textarea-length">{csDecs.length}/1000</div>
          </div>
          {children}
        </div>
      </Drawer>
      <SelectTimePop
        typeName={type}
        startTime={startTime}
        setStartTime={setStartTime}
        endTime={endTime}
        setEndTime={setEndTime}
      />
    </>
  );
};

export default memo(ChooseTimePicker);
